// Sign-in for the standalone desk. Plain forms drawn from the same tokens as the desk, so they look native in
// both schemes. Nothing here stores a password: it goes to the server, which hashes it.
import { useRef, useState, type FormEvent } from 'react';
import { setChoice, useResolvedScheme } from './theme';

export type SessionUser = { id: string; firstName: string; lastName: string; email: string; role: 'SUPER_ADMIN' | 'ADMIN' | 'TENANT_ADMIN' | 'COUNSELLOR' | 'TELECALLER'; isActive: boolean; reportsTo: string | null };

export const LoginThemeToggle = () => {
  const scheme = useResolvedScheme();
  const [hovered, setHovered] = useState(false);

  const isDark = scheme === 'dark';
  const toggle = () => {
    setChoice(isDark ? 'light' : 'dark');
  };

  return (
    <button
      type="button"
      onClick={toggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 100,
        padding: '6px 12px',
        borderRadius: 'var(--t-border-radius-sm, 6px)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 500,
        background: hovered ? 'var(--t-background-tertiary)' : 'var(--t-background-primary)',
        border: '1px solid var(--t-border-color-medium)',
        color: 'var(--t-font-color-secondary)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.15s ease',
        userSelect: 'none',
      }}
    >
      {isDark ? 'Light mode' : 'Dark mode'}
    </button>
  );
};

const post = async (path: string, body: Record<string, unknown>): Promise<SessionUser> => {
  const response = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const data = (await response.json().catch(() => ({}))) as SessionUser & { error?: string };

  if (!response.ok) throw new Error(data.error ?? `The server answered ${response.status}.`);

  return data;
};

const field = {
  width: '100%',
  boxSizing: 'border-box' as const,
  padding: '9px 10px',
  borderRadius: 'var(--t-border-radius-sm)',
  border: '1px solid var(--t-border-color-medium)',
  background: 'var(--t-background-primary)',
  color: 'var(--t-font-color-primary)',
  fontSize: 'var(--t-font-size-md)',
};

const Label = ({ id, text, children }: { id: string; text: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 14 }}>
    <label htmlFor={id} style={{ display: 'block', fontSize: 'var(--t-font-size-sm)', fontWeight: 500, color: 'var(--t-font-color-secondary)', marginBottom: 4 }}>
      {text}
    </label>
    {children}
  </div>
);

const Shell = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'var(--t-background-secondary)', position: 'relative' }}>
    <div style={{ width: '100%', maxWidth: 400 }}>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src="/ewings-logo-light.svg" alt="eWings" className="ewings-brand-logo-light" style={{ height: 48, width: 'auto', maxWidth: 240, objectFit: 'contain' }} />
          <img src="/ewings-logo-dark.svg" alt="eWings" className="ewings-brand-logo-dark" style={{ height: 48, width: 'auto', maxWidth: 240, objectFit: 'contain' }} />
        </div>
      </div>
      <div style={{ background: 'var(--t-background-primary)', border: '1px solid var(--t-border-color-medium)', borderRadius: 'var(--t-border-radius-md)', padding: 24, boxShadow: 'var(--t-box-shadow-strong)' }}>
        <div style={{ fontSize: 'var(--t-font-size-lg)', fontWeight: 600, color: 'var(--t-font-color-primary)' }}>{title}</div>
        <div style={{ fontSize: 'var(--t-font-size-md)', color: 'var(--t-font-color-secondary)', margin: '4px 0 20px' }}>{subtitle}</div>
        {children}
      </div>
    </div>
    <LoginThemeToggle />
  </div>
);

const Submit = ({ busy, children }: { busy: boolean; children: React.ReactNode }) => (
  <button
    type="submit"
    disabled={busy}
    style={{ width: '100%', padding: '10px 0', border: 'none', borderRadius: 'var(--t-border-radius-sm)', background: 'var(--t-color-blue, #071c3c)', color: '#fff', fontSize: 'var(--t-font-size-md)', fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
  >
    {busy ? 'Signing in…' : children}
  </button>
);

const Problem = ({ text }: { text: string | null }) =>
  text === null ? null : (
    <div role="alert" style={{ margin: '0 0 14px', padding: '8px 10px', borderRadius: 'var(--t-border-radius-sm)', background: 'var(--t-tag-background-red)', color: 'var(--t-tag-text-red)', fontSize: 'var(--t-font-size-sm)' }}>
      {text}
    </div>
  );

export const AuthPage = ({
  notice = null,
  onSignedIn,
}: {
  initialTab?: string;
  notice?: string | null;
  setupNeeded?: boolean;
  onSignedIn: (user: SessionUser) => void;
}) => {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordBox = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    if (loginEmail.trim() === '') return void setError('Enter your email address.');
    if (loginPassword === '') return void setError('Enter your password.');

    setBusy(true);
    setError(null);

    try {
      onSignedIn(await post('/api/auth/login', { email: loginEmail, password: loginPassword }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not sign in.');
      setLoginPassword('');
      passwordBox.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Sign in"
      subtitle="Use your Lead Desk account to sign in."
    >
      <form onSubmit={(event) => void handleLogin(event)} noValidate>
        {notice !== null && error === null && (
          <div role="status" style={{ margin: '0 0 14px', padding: '8px 10px', borderRadius: 'var(--t-border-radius-sm)', background: 'var(--t-tag-background-yellow)', color: 'var(--t-tag-text-yellow)', fontSize: 'var(--t-font-size-sm)' }}>
            {notice}
          </div>
        )}
        <Problem text={error} />
        <Label id="login-email" text="Email address">
          <input
            id="login-email"
            style={field}
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={loginEmail}
            onChange={(event) => setLoginEmail(event.target.value)}
          />
        </Label>
        <Label id="login-password" text="Password">
          <div style={{ position: 'relative' }}>
            <input
              id="login-password"
              ref={passwordBox}
              style={{ ...field, paddingRight: 56 }}
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ position: 'absolute', right: 6, top: 6, border: 'none', background: 'transparent', color: 'var(--t-font-color-secondary)', fontSize: 'var(--t-font-size-sm)', cursor: 'pointer' }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </Label>
        <Submit busy={busy}>Sign in</Submit>

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 'var(--t-font-size-xs)', color: 'var(--t-font-color-tertiary)' }}>
          Need an account? Contact your administrator to request access.
        </div>
      </form>
    </Shell>
  );
};

export const LoginPage = ({ notice, onSignedIn }: { notice: string | null; onSignedIn: (user: SessionUser) => void }) => (
  <AuthPage notice={notice} onSignedIn={onSignedIn} />
);

export const SetupPage = ({ onSignedIn }: { onSignedIn: (user: SessionUser) => void }) => (
  <AuthPage notice={null} onSignedIn={onSignedIn} />
);
